import axios from 'axios'
import toast from 'react-hot-toast'

const defaultApiBaseUrl = import.meta.env.DEV
  ? `http://${window.location.hostname || 'localhost'}:3001/api`
  : '/api'

const API_BASE_URL = (import.meta.env.VITE_API_URL || defaultApiBaseUrl).replace(/\/+$/, '')

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 10000,
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // FormData must not use application/json — multer needs multipart with boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type']
        delete config.headers['content-type']
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      sessionStorage.removeItem('token')
      delete api.defaults.headers.common['Authorization']
      const path = window.location.pathname || ''
      const publicAuthPaths = ['/login', '/registro', '/forgot-password', '/reset-password']
      if (!publicAuthPaths.some((p) => path === p || path.startsWith(`${p}/`))) {
        window.location.href = '/login'
      }
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'SUBSCRIPTION_PAYMENT_REQUIRED'
    ) {
      const path = window.location.pathname || ''
      if (path !== '/completar-pago') {
        window.location.href = '/completar-pago'
      }
    }
    
    if (error.response?.status >= 500) {
      toast.error('Error del servidor. Por favor, intente más tarde.')
    }
    
    return Promise.reject(error)
  }
)

// API endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  signupSendCode: (payload) => api.post('/auth/signup/send-code', payload),
  signupConfirm: (payload) => api.post('/auth/signup/confirm', payload),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetCode: (payload) => api.post('/auth/verify-reset-code', payload),
  resetPasswordWithCode: (payload) => api.post('/auth/reset-password', payload),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (userData) => api.put('/auth/profile', userData),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
  endImpersonation: (data) => api.post('/auth/impersonation/end', data || {}),
}

export const subscriptionAPI = {
  listPlans: () => api.get('/subscription/plans'),
  getCurrent: () => api.get('/subscription/current'),
  getCheckoutStatus: (sessionId) =>
    api.get('/subscription/checkout-status', {
      params: sessionId ? { session_id: sessionId } : {},
    }),
}

/** Panel del dueño del software: funerarias (tenants) y usuarios por funeraria */
export const platformAPI = {
  getStats: () => api.get('/platform/stats'),
  listOrganizations: () => api.get('/platform/organizations'),
  createOrganization: (data) => api.post('/platform/organizations', data),
  updateOrganization: (organizationId, data) =>
    api.patch(`/platform/organizations/${organizationId}`, data),
  listIncidentReports: (params) => api.get('/platform/incident-reports', { params }),
  getIncidentReport: (id) => api.get(`/platform/incident-reports/${id}`),
  patchIncidentReport: (id, data) => api.patch(`/platform/incident-reports/${id}`, data),
  listTenants: (params) => api.get('/platform/tenants', { params }),
  createTenant: (data) => api.post('/platform/tenants', data),
  updateTenant: (id, data) => api.patch(`/platform/tenants/${id}`, data),
  updateTenantStatus: (id, data) => api.patch(`/platform/tenants/${id}/status`, data),
  getTenantProfile: (id) => api.get(`/platform/tenants/${id}/profile`),
  listTenantAudit: (id, params) => api.get(`/platform/tenants/${id}/audit`, { params }),
  startTenantImpersonation: (tenantId, data) =>
    api.post(`/platform/tenants/${tenantId}/impersonation/start`, data || {}),
  listTenantUsers: (tenantId) => api.get(`/platform/tenants/${tenantId}/users`),
  createTenantUser: (tenantId, data) => api.post(`/platform/tenants/${tenantId}/users`, data),
  updateTenantUser: (tenantId, userId, data) =>
    api.patch(`/platform/tenants/${tenantId}/users/${userId}`, data),
  toggleTenantUserStatus: (tenantId, userId) =>
    api.put(`/platform/tenants/${tenantId}/users/${userId}/toggle-status`),
  resetTenantUserPassword: (tenantId, userId, data) =>
    api.put(`/platform/tenants/${tenantId}/users/${userId}/reset-password`, data),
  resendTenantCredentials: (tenantId, userId) =>
    api.post(`/platform/tenants/${tenantId}/users/${userId}/resend-credentials`),
  deleteTenantUser: (tenantId, userId) =>
    api.delete(`/platform/tenants/${tenantId}/users/${userId}`),
}

export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getStats: () => api.get('/clients/stats'),
  getFamilyMembers: (id) => api.get(`/clients/${id}/family-members`),
  getClientPlans: (id) => api.get(`/clients/${id}/plans`),
  getPayments: (id) => api.get(`/clients/${id}/payments`),
  downloadTemplate: (format = 'xlsx') =>
    api.get('/clients/export/template', { params: { format }, responseType: 'blob' }),
  export: (params) => api.get('/clients/export', { params, responseType: 'blob' }),
  importPreview: (formData) => api.post('/clients/import/preview', formData),
  import: (formData) => api.post('/clients/import', formData),
}


export const planTemplatesAPI = {
  getAll: (params) => api.get('/plan-templates', { params }),
  getById: (id) => api.get(`/plan-templates/${id}`),
  create: (data) => api.post('/plan-templates', data),
  update: (id, data) => api.put(`/plan-templates/${id}`, data),
  delete: (id) => api.delete(`/plan-templates/${id}`),
  getStats: () => api.get('/plan-templates/stats'),
}

export const familyMembersAPI = {
  getAll: (params) => api.get('/family-members', { params }),
  getById: (id) => api.get(`/family-members/${id}`),
  create: (data) => api.post('/family-members', data),
  update: (id, data) => api.put(`/family-members/${id}`, data),
  delete: (id) => api.delete(`/family-members/${id}`),
  getStats: () => api.get('/family-members/stats'),
  getByClient: (clientId) => api.get(`/family-members/client/${clientId}`),
  getByPlan: (planId) => api.get(`/family-members/plan/${planId}`),
}

export const casketsAPI = {
  getAll: (params) => api.get('/caskets', { params }),
  getById: (id) => api.get(`/caskets/${id}`),
  create: (data) => api.post('/caskets', data),
  update: (id, data) => api.put(`/caskets/${id}`, data),
  delete: (id) => api.delete(`/caskets/${id}`),
  updateAvailability: (id, isAvailable) => api.put(`/caskets/${id}/availability`, { isAvailable }),
  adjustStock: (id, delta, reason) => api.put(`/caskets/${id}/stock`, { delta, reason }),
  getStats: () => api.get('/caskets/stats'),
}

export const casketSalesAPI = {
  getAll: (params) => api.get('/casket-sales', { params }),
  getById: (id) => api.get(`/casket-sales/${id}`),
  create: (data) => api.post('/casket-sales', data),
  downloadReceipt: (id) => api.get(`/casket-sales/${id}/receipt`, { responseType: 'blob' }),
}

export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getStats: (params) => api.get('/invoices/stats', { params }),
  getPending: (clientId) => api.get('/invoices/pending', { params: clientId ? { clientId } : {} }),
  getOverdue: () => api.get('/invoices/overdue'),
  getClientInvoices: (clientId, status) => api.get(`/invoices/client/${clientId}`, { params: status ? { status } : {} }),
  getClientReceivables: (clientId) => api.get(`/invoices/client/${clientId}/receivables`),
  getClientCredit: (clientId) => api.get(`/invoices/client/${clientId}/credit`),
  markAsPaid: (id) => api.put(`/invoices/${id}/paid`),
  markAsOverdue: (id) => api.put(`/invoices/${id}/overdue`),
  emit: (id) => api.put(`/invoices/${id}/emit`),
  recalculateStatus: (id) => api.put(`/invoices/${id}/recalculate-status`),
  generateMonthly: () => api.post('/invoices/generate-monthly'),
  updateOverdue: () => api.put('/invoices/update-overdue'),
  generatePDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  viewPDF: (id) => api.get(`/invoices/${id}/view-pdf`, { responseType: 'blob' }),
}

export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  verify: (id) => api.put(`/payments/${id}/verify`),
  confirm: (id, data) => api.put(`/payments/${id}/confirm`, data),
  apply: (id, data) => api.put(`/payments/${id}/apply`, data),
  cancel: (id, data) => api.put(`/payments/${id}/cancel`, data),
  refund: (id, data) => api.post(`/payments/${id}/refund`, data),
  getClientHistory: (clientId) => api.get(`/payments/client/${clientId}`),
  getClientStatement: (clientId) => api.get(`/payments/client/${clientId}/statement`),
  getInvoiceHistory: (invoiceId) => api.get(`/payments/invoice/${invoiceId}`),
  getPendingInvoices: (clientId) => api.get(`/payments/client/${clientId}/pending-invoices`),
  getStats: (params) => api.get('/payments/stats', { params }),
  uploadReceipt: (file) => {
    const formData = new FormData();
    formData.append('receipt', file);

    return api.post('/payments/upload-receipt', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  downloadReceipt: (id) => api.get(`/payments/${id}/receipt`, { responseType: 'blob' }),
}

export const paymentIntentsAPI = {
  getAll: (params) => api.get('/payment-intents', { params }),
  getById: (id) => api.get(`/payment-intents/${id}`),
  create: (data) => api.post('/payment-intents', data),
}

export const dashboardAPI = {
  get: () => api.get('/dashboard'),
}

export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getRevenue: (params) => api.get('/reports/revenue', { params }),
  getPaymentsPeriod: (params) => api.get('/reports/payments-period', { params }),
  getReceivables: () => api.get('/reports/receivables'),
  getStatement: (clientId) => api.get(`/reports/statement/${clientId}`),
  getClients: (params) => api.get('/reports/clients', { params }),
  getPlans: (params) => api.get('/reports/plans', { params }),
  getInventory: () => api.get('/reports/inventory'),
  generate: (data) => api.post('/reports/generate', data, { responseType: 'blob' }),
}

export const configurationAPI = {
  get: () => api.get('/configuration'),
  getTheme: () => api.get('/configuration/theme'),
  update: (data) => api.put('/configuration', data),
  uploadLogo: (formData) => api.post('/configuration/logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteLogo: () => api.delete('/configuration/logo'),
  resetToDefaults: () => api.post('/configuration/reset'),
}

export const fiscalAPI = {
  getDashboard: () => api.get('/fiscal/dashboard'),
  getProfile: () => api.get('/fiscal/profile'),
  updateProfile: (data) => api.put('/fiscal/profile', data),
  getSequences: () => api.get('/fiscal/sequences'),
  previewSequence: (ecfType) => api.get(`/fiscal/sequences/${ecfType}/preview`),
  enableSequence: (ecfType) => api.post(`/fiscal/sequences/${ecfType}/enable`),
  disableSequence: (ecfType) => api.post(`/fiscal/sequences/${ecfType}/disable`),
  updateSequenceAuthorization: (ecfType, data) =>
    api.put(`/fiscal/sequences/${ecfType}/authorization`, data),
  getBilling: () => api.get('/fiscal/billing'),
  updateBilling: (data) => api.put('/fiscal/billing', data),
  getTaxes: () => api.get('/fiscal/taxes'),
  createTax: (data) => api.post('/fiscal/taxes', data),
  updateTax: (id, data) => api.put(`/fiscal/taxes/${id}`, data),
  getElectronic: () => api.get('/fiscal/electronic'),
  updateElectronic: (data) => api.put('/fiscal/electronic', data),
  uploadCertificate: (formData) => api.post('/fiscal/certificate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getReadiness: () => api.get('/fiscal/readiness'),
  listDocuments: (params) => api.get('/fiscal/documents', { params }),
  issueFromInvoice: (invoiceId, data) =>
    api.post(`/fiscal/documents/issue-from-invoice/${invoiceId}`, data || {}),
  refreshDocumentStatus: (id) => api.post(`/fiscal/documents/${id}/refresh-status`),
}

export const emailNotificationsAPI = {
  getSettings: () => api.get('/email-notifications/settings'),
  updateSettings: (data) => api.put('/email-notifications/settings', data),
  getLogs: (params) => api.get('/email-notifications/logs', { params }),
  sendNow: (data) => api.post('/email-notifications/send-now', data || {}),
  sendTest: (data) => api.post('/email-notifications/send-test', data),
  retryLog: (id) => api.post(`/email-notifications/logs/${id}/retry`),
}

export const permissionsAPI = {
  getCurrentUserPermissions: () => api.get('/permissions/me'),
  check: (moduleName, action) => api.get('/permissions/check', { params: { moduleName, action } }),
}

export const auditLogsAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  getById: (id) => api.get(`/audit-logs/${id}`),
}

/** Reportes de incidencias / soporte (usuarios de funeraria) */
export const incidentReportsAPI = {
  getMeta: () => api.get('/incident-reports/meta'),
  create: (formData) =>
    api.post('/incident-reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }),
}

export const serviceActivationsAPI = {
  getAll: (params) => api.get('/service-activations', { params }),
  getStats: () => api.get('/service-activations/stats'),
  getById: (id) => api.get(`/service-activations/${id}`),
  create: (data) => api.post('/service-activations', data),
  updateStatus: (id, status) => api.put(`/service-activations/${id}/status`, { status }),
  cancel: (id) => api.delete(`/service-activations/${id}/cancel`),
}

/** Origen del backend para enlaces a /uploads (adjuntos de incidencias, etc.) */
export function getApiOrigin() {
  const base = (import.meta.env.VITE_API_URL || defaultApiBaseUrl).replace(/\/+$/, '')
  return base.replace(/\/api\/?$/i, '') || ''
}
